import { useEffect, useState } from "react";
import "./giantModal.css";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { ReactComponent as Arrow } from "../../../../assets/icons/green-arrow.svg";
import VerticalCard from "../../cards/verticalCard/VerticalCard";
import { useNavigate } from "react-router-dom";
import { getIcon } from "../../../../utils/iconConfig";
import {
  useCategories,
  useDesigns,
  useSubCategories,
} from "./apis/categoryAPIs";
import { useGlobalContext } from "../../../../context/GlobalContext";
import { useInView } from "react-intersection-observer";
import TabSkeleton from "../../TabSkeleton";
import VerticalCardSkeleton from "../../skeletons/verticalCardSkeleton";
import { ReactComponent as Close } from "../../../../assets/icons/boxed-close.svg";
import { fetchItemSizes } from "../../cards/longCard/apis/itemSize";
import { DESIGN_SECTIONS } from "../../../../features/cart/api/designStats";
import ModalShell from "../ModalShell";

const ChildCard = ({ name, count, handleClick, added }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "12.2vw",
        height: "2.75vw",
        background: added ? "#50C87814" : "#0D0D0D",
        color: "#FFFFFF",
        borderRadius: ".25vw",
        padding: "1vw",
        gap: ".5vw",
        cursor: "pointer",
      }}
      onClick={() => handleClick(name, added)}
    >
      <Typography
        fontSize={".9vw"}
        fontWeight={500}
        fontFamily={"Karla"}
        lineHeight="1.1vw"
      >
        {name}
      </Typography>
      <Typography
        fontSize={".9vw"}
        fontWeight={500}
        fontFamily={"Karla"}
        lineHeight="1.1vw"
      >
        {count}
      </Typography>
    </div>
  );
};

const GiantModal = ({
  openModal,
  handleClose,
  setsDesigns,
  isLoading,
  embedded,
  tabSwitcher,
}) => {
  const navigate = useNavigate();

  const { ref: ScrollRef, inView: LoaderInView } = useInView({
    threshold: 0.4,
    triggerOnce: false,
  });

  const [header, setHeader] = useState(["Selected Categories"]);

  const [navigationContext, setNavigationContext] = useState(
    "Selected Categories"
  );

  const {
    cartData: globalCartData,
    setCartData: setSessionData,
    handleRemoveFromCart,
    setSelectedCard,
  } = useGlobalContext();

  const { data: categories, isLoading: loadingCategories } = useCategories();

  const { data: subCategories, isLoading: loadingSubCategories } =
    useSubCategories({ categoryName: header[1] });

  const {
    data: designs,
    isLoading: loadingDesigns,
    fetchNextPage,
    hasNextPage,
  } = useDesigns({
    categoryName: header[1],
    subCategoryName: header[2],
  });

  const [tiles, setTiles] = useState(categories);
  const [result, setResult] = useState([]);
  const [names, setNames] = useState(new Set());
  const [allDesigns, setAllDesigns] = useState([]);

  useEffect(() => {
    if (header.length === 1) {
      setTiles(categories);
      setResult(categoryResult);
      setNames(resultNames);
    } else if (header.length === 2) {
      setTiles(subCategories);
      setResult(subCategoryResult);
      setNames(subResultNames);
    } else {
      setTiles([]);
      setResult([]);
      setNames([]);
    }
  }, [header, categories, subCategories, globalCartData]);

  useEffect(() => {
    if (LoaderInView && hasNextPage) {
      fetchNextPage();
      return;
    }
  }, [LoaderInView, hasNextPage, fetchNextPage]);

  const handleHeaderClick = (clickedHeader) => {
    const index = header.indexOf(clickedHeader);
    if (index !== -1) {
      const newHeader = header.slice(0, index + 1);
      setHeader(newHeader);

      if (newHeader.length === 1) {
        setHeader(["Selected Categories"]);
      }
    }
  };

  const handleClick = (categoryName, fromMissedSection) => {
    let newHeader;
    let newContext;

    if (fromMissedSection) {
      newHeader = [...header, categoryName];
      newContext = "Selected Categories";
    } else {
      newHeader = [...header, categoryName];
      newContext = "Missed Categories";
    }

    setHeader(newHeader);
    setNavigationContext(newContext);
  };

  const categoryCounts = globalCartData.reduce((acc, item) => {
    const category = item.categoryName;
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  const categoryResult = Object.entries(categoryCounts).map(
    ([name, count]) => ({
      name,
      count,
    })
  );

  const resultNames = new Set(categoryResult?.map((item) => item.name));

  //-----------------------------------------------------------------------------------------------------------------------//

  const selectedCategory = globalCartData?.filter(
    (design, index) => design.categoryName === header[1]
  );

  const subCategoryCounts = selectedCategory?.reduce((acc, item) => {
    const subCategoryName = item?.subCategoryName;
    acc[subCategoryName] = (acc[subCategoryName] || 0) + 1;
    return acc;
  }, {});

  const subCategoryResult = Object?.entries(subCategoryCounts).map(
    ([name, count]) => ({
      name,
      count,
    })
  );

  const subResultNames = new Set(subCategoryResult?.map((item) => item.name));

  const handleAdd = async (card) => {
    const categoryName = card?.categoryName;
    const sizeData = await fetchItemSizes({
      queryKey: [`item-sizes-${categoryName}`, { categoryName }],
    });
    let designs = JSON.parse(localStorage.getItem("cartItems")) || [];
    const newDesign = {
      ...card,
      sizeAvailable: sizeData?.length > 0,
      // Picked from the category browser. Counted per section once the order
      // is placed.
      addSection: DESIGN_SECTIONS.CATEGORIES,
      addedAt: new Date().toISOString(),
      quantityData: [
        {
          key: `${card?.styleNumber}-${Date.now()}`,
          itemSize: card?.subCategoryName === "Without Chain" ? "N/A" :card?.itemSize || "",
          goldColor: "P",
          goldQty: "18",
          diaQty: "VS-GH",
          message: null,
          rhodium: "W",
        },
      ],
      quantity: 1,
    };

    designs?.push(newDesign);
    localStorage.setItem("cartItems", JSON.stringify(designs));
    setSelectedCard(designs?.[designs?.length - 1]?.styleNumber);
    setSessionData(JSON.parse(localStorage.getItem("cartItems")));
  };

  const handleRemove = (card) => {
    let designs = JSON.parse(localStorage.getItem("cartItems"));
    designs = designs.filter((item) => item?.styleNumber !== card?.styleNumber);
    localStorage.setItem("cartItems", JSON.stringify(designs));
    setSelectedCard(designs?.[designs?.length - 1]?.styleNumber);
    setSessionData(JSON.parse(localStorage.getItem("cartItems")));
  };

  useEffect(() => {
    if (designs?.pages) {
      const flattenedDesigns = designs.pages.flatMap((page) => page.designs);
      setAllDesigns(flattenedDesigns);
    }
  }, [designs]);

  return (
    <ModalShell embedded={embedded} open={openModal}>
        <Box
          sx={{
            position: embedded ? "relative" : "absolute",
            top: embedded ? 0 : "12%",
            background: "#141414",
            borderRadius: ".4vw",
            outline: "none",
            border: "none",
            width: embedded ? "100%" : "96vw",
            height: embedded ? "100%" : "84vh",
          }}
          marginX="auto"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              width: "100%",
              height: header.length === 1 ? "100%" : "96%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "1.2vw",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "1.5vw" }}>
              {tabSwitcher}
              <Typography
                fontSize={".9vw"}
                fontWeight={500}
                fontFamily={"Karla"}
                lineHeight={"1.1vw"}
                sx={{
                  cursor: "pointer",
                  display:
                    header.length === 1 && !result?.length ? "none" : "block",
                }}
              >
                {header?.map((item, index) => (
                  <span
                    key={index}
                    onClick={() => handleHeaderClick(item)}
                    style={{
                      color:
                        index === header?.length - 1 ? "#FFFFFF" : "#595959",
                    }}
                  >
                    {index === 0 && header.length > 1
                      ? "Categories"
                      : index === 1 && header.length === 2
                      ? `${result?.length > 0 ? "Selected " : ""}Sub-Categories of ${item}`
                      : item}
                    {index < header?.length - 1 && " \u00A0>\u00A0 "}
                  </span>
                ))}
                {result?.length > 0 && (
                  <span
                    style={{
                      cursor: "pointer",
                      fontSize: ".9vw",
                      fontWeight: 500,
                      fontFamily: "Karla",
                      lineHeight: "1.1vw",
                      color: "white",
                    }}
                  >
                    {" "}
                    - {result?.length}
                  </span>
                )}
              </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: ".7vw",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: ".25vw",
                    padding: ".15vw .6vw",
                    borderRadius: ".25vw",
                    background: "#262626",
                  }}
                >
                  {getIcon("Ring")}
                  <Typography
                    fontFamily={"Karla"}
                    fontSize={".7vw"}
                    fontWeight={"400"}
                    color={"#FCFAFA"}
                  >
                    Design{globalCartData.length > 1 ? "s" : ""} :
                  </Typography>
                  <Typography
                    fontFamily={"Poppins"}
                    fontSize={".8vw"}
                    fontWeight={"600"}
                    color={"#FCFAFA"}
                  >
                    {globalCartData.length}
                  </Typography>
                </Box>
                <Close
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    handleClose();
                    setHeader(["Selected Categories"]);
                    setNavigationContext("Selected Categories");
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ overflowY: "scroll", flexGrow: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: "1.5vw",
                }}
              >
                {/* Section for items with added !== false */}
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  sx={{
                    flex: "1 1 1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "1.6vw",
                    rowGap: "1.6vw",
                    color: "#fff",
                    padding: ".4vw 1vw",
                  }}
                >
                  {header[0] === "Missed Categories" && loadingDesigns ? (
                    // <CircularProgress color={"success"} />
                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "50vh",
                      }}
                    >
                      <CircularProgress color="success" />
                    </Grid>
                  ) : designs ? (
                    designs?.pages?.map((page) =>
                      page?.designs?.map((design) => {
                        header[0] === "Missed Categories" && (
                          <>
                            <VerticalCard
                              key={design?._id}
                              data={design}
                              color="#262626"
                              onAdd={handleAdd}
                              allData={allDesigns}
                              onRemove={handleRemove}
                              added={globalCartData?.some(
                                (item) =>
                                  String(item?.styleNumber)
                                    .trim()
                                    .toLowerCase() ===
                                  String(design?.styleNumber)
                                    .trim()
                                    .toLowerCase()
                              )}
                            />
                          </>
                        );
                      })
                    )
                  ) : loadingCategories || loadingSubCategories ? (
                    <TabSkeleton
                      height={"2.75vw"}
                      width={"12.2vw"}
                      count={10}
                    />
                  ) : (
                    result?.map((category, index) => (
                      <ChildCard
                        key={index}
                        name={category.name}
                        count={category.count}
                        handleClick={handleClick}
                        added={true}
                      />
                    ))
                  )}
                </Grid>
                {header.length === 3 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: ".5vw",
                    }}
                  >
                    {header[0] === "Missed Categories" && hasNextPage && (
                      <CircularProgress ref={ScrollRef} />
                    )}
                  </div>
                )}

                {header?.length < 3 && result?.length > 0 && (
                  <Divider
                    orientation="horizontal"
                    flexItem
                    style={{
                      border: ".15vw  solid",
                      borderColor: "#262626",
                      width: "100%",
                      margin: "1.5vw 0",
                    }}
                  />
                )}

                {header?.length < 3 && result?.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingInline: "1vw",
                      paddingBottom: "1vw",
                      color: "white",
                    }}
                  >
                    <Typography
                      fontSize={".9vw"}
                      fontWeight={500}
                      fontFamily={"Karla"}
                      lineHeight={"1.1vw"}
                      sx={{
                        cursor: "pointer",
                        color: header.length > 1 ? "#595959" : "#FFFFFF",
                      }}
                    >
                      {header.length > 1 ? "" : "Missed Categories"}
                      {header?.map((item, index) => (
                        <span
                          key={index}
                          onClick={() => handleHeaderClick(item)}
                          style={{
                            color:
                              index === header?.length - 1
                                ? "#FFFFFF"
                                : "#595959",
                          }}
                        >
                          {index === 1
                            ? `Missed Sub-Categories of ${item}`
                            : ""}
                        </span>
                      ))}
                      {tiles?.length > 0 && (
                        <span
                          style={{
                            cursor: "pointer",
                            fontSize: ".9vw",
                            fontWeight: 500,
                            fontFamily: "Karla",
                            lineHeight: "1.1vw",
                            color: "white",
                          }}
                        >
                          {" "}
                          - {tiles?.length - result?.length}
                        </span>
                      )}
                    </Typography>
                  </Box>
                )}

                {/* Section for items with added === false */}
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  sx={{
                    flex: "1 1 1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "1.6vw",
                    rowGap: "1.6vw",
                    color: "#fff",
                    padding: "0 1vw",
                  }}
                >
                  {header[0] === "Selected Categories" && loadingDesigns ? (
                    // <CircularProgress color={"success"} />
                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "50vh",
                      }}
                    >
                      <CircularProgress color="success" />
                    </Grid>
                  ) : designs ? (
                    designs?.pages?.map((page) =>
                      page?.designs?.map(
                        (design) =>
                          header[0] === "Selected Categories" && (
                            <>
                              <VerticalCard
                                key={design?._id}
                                data={design}
                                color="#262626"
                                onAdd={handleAdd}
                                allData={allDesigns}
                                onRemove={handleRemove}
                                added={globalCartData?.some(
                                  (item) =>
                                    String(item?.styleNumber)
                                      .trim()
                                      .toLowerCase() ===
                                    String(design?.styleNumber)
                                      .trim()
                                      .toLowerCase()
                                )}
                              />
                            </>
                          )
                      )
                    )
                  ) : loadingCategories || loadingSubCategories ? (
                    <TabSkeleton
                      height={"2.75vw"}
                      width={"12.2vw"}
                      count={10}
                    />
                  ) : (
                    tiles
                      ?.filter((category) => !names?.has(category))
                      .map((category, index) => (
                        <ChildCard
                          key={index}
                          name={category}
                          handleClick={handleClick}
                          added={false}
                        />
                      ))
                  )}
                </Grid>
                {header?.length === 3 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: "1.6vw",
                      padding: "0 1vw",
                    }}
                    ref={ScrollRef}
                  >
                    {header[0] === "Selected Categories" && hasNextPage && (
                      <VerticalCardSkeleton value={7} gap={"1.6vw"} />
                    )}
                  </div>
                )}
              </Box>
            </Box>

            {header?.length === 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  cursor: "pointer",
                  marginX: ".7vw",
                  marginBottom: ".7vw",
                  height: "2vw",
                }}
                onClick={() => navigate("/cart")}
              >
                <Button
                  sx={{
                    color: "#50C878",
                    textTransform: "none",
                    fontFamily: "Poppins",
                    fontSize: ".7vw",
                    fontWeight: "400",
                    height: "2vw !important",
                    minWidth: "7vw",
                    padding: 0,
                  }}
                >
                  Go to Cart <Arrow style={{ marginLeft: ".25vw" }} />
                </Button>
              </Box>
            )}
          </Box>
          {header?.length === 3 && (
            <Button
              sx={{
                width: "100%",
                height: "1.75vw",
                background: "#50C878",
                color: "#000000",
                fontFamily: "Poppins",
                fontSize: ".8vw",
                fontWeight: "600",
                borderRadius: 0,
                borderBottomLeftRadius: ".4vw",
                borderBottomRightRadius: ".4vw",
              }}
              onClick={() => {
                handleClose();
                setHeader(["Selected Categories"]);
                setNavigationContext("Selected Categories");
              }}
            >
              PROCEED
            </Button>
          )}
        </Box>
    </ModalShell>
  );
};

export default GiantModal;

